import { z } from 'zod';

const moneyDto = z.object({
  amount: z.number(),
  currency: z.enum(['GEL', 'USD', 'EUR']),
});

const imageDto = z.object({
  url: z.string().url(),
  alt: z.string().default(''),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
});

const priceBreakdownDto = z.object({
  subtotal: moneyDto,
  discount: moneyDto,
  shipping: moneyDto,
  tax: moneyDto,
  total: moneyDto,
});

const promotionKindDto = z.discriminatedUnion('type', [
  z.object({ type: z.literal('percent-off'), percent: z.number().min(0).max(100) }),
  z.object({ type: z.literal('amount-off'), amount: moneyDto }),
  z.object({ type: z.literal('free-shipping') }),
  z.object({ type: z.literal('gift'), description: z.string() }),
]);

const promotionDto = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  code: z.string().nullable().default(null),
  label: z.string(),
  kind: promotionKindDto,
  starts_at: z.string().nullable().default(null),
  ends_at: z.string().nullable().default(null),
});

const appliedPromotionDto = z.object({
  promotion: promotionDto,
  discount_amount: moneyDto,
});

const cartItemCharacteristicDto = z.object({
  name: z.string(),
  value: z.string(),
});

const cartItemDto = z.object({
  product_id: z.union([z.string(), z.number()]).transform(String),
  sku: z.union([z.string(), z.number()]).transform(String),
  meta_catalog: z.object({ content_id: z.string().optional(), content_brand: z.string(), product_type: z.string(), content_category: z.string() }).optional(),
  slug: z.string().nullable().default(null),
  title: z.string(),
  image: imageDto.nullable().default(null),
  characteristics: z.array(cartItemCharacteristicDto).default([]),
  unit_price: moneyDto,
  compare_at_price: moneyDto.nullable().default(null),
  quantity: z.number().int().min(0),
  line_total: moneyDto,
  promotion_discount: moneyDto.nullable().default(null),
  available_quantity: z.number().int().min(0).nullable().default(null),
});

const collectionCartItemDto = z.object({
  collection_id: z.string(), slug: z.string(), title: z.string(),
  image: imageDto.nullable(), quantity: z.number().int().positive(),
  unit_price: moneyDto, compare_at_price: moneyDto, line_total: moneyDto,
  available_quantity: z.number().int().min(0),
  components: z.array(z.object({
    product_id: z.string(), title: z.string(), quantity: z.number().int(),
    sku: z.string().optional(),
    meta_catalog: z.object({ content_id: z.string().optional(), content_brand: z.string(), product_type: z.string(), content_category: z.string() }).optional(),
    unit_price: moneyDto,
  })),
  coupon_policy: z.string(),
});

export const cartDto = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  items: z.array(cartItemDto).default([]),
  collections: z.array(collectionCartItemDto).default([]),
  item_count: z.number().int().min(0).default(0),
  currency: z.enum(['GEL', 'USD', 'EUR']),
  totals: priceBreakdownDto,
  promotions: z.array(appliedPromotionDto).default([]),
  promotion_progress: z.array(z.object({
    promotion_id: z.string(), label: z.string(),
    eligible_quantity: z.number().int().min(0),
    required_quantity: z.number().int().positive(),
    remaining_quantity: z.number().int().min(0),
    unlocked: z.boolean(),
    discount_type: z.enum(['percent', 'fixed']),
    discount_value: z.number().positive(),
  })).default([]),
  updated_at: z.string(),
});

export type CartDto = z.infer<typeof cartDto>;
export type CartItemDto = z.infer<typeof cartItemDto>;
export type AppliedPromotionDto = z.infer<typeof appliedPromotionDto>;
export type PromotionDto = z.infer<typeof promotionDto>;
